import React from "react";
import {
  ButtonsContainer,
  CategoryTitle,
  Price,
  Title,
  TitlePriceWrapper,
  TotalPrice,
  TotalTitle,
  ValueWrapper,
  ValuesWrapper,
  Warnings,
  Wrapper,
} from "./styles";
import formatValue from "../../../../utils/formatValue";
import { FaPercentage, FaWhatsapp } from "react-icons/fa";
import { BiBuilding, BiPhoneCall } from "react-icons/bi";
import { MdInfoOutline } from "react-icons/md";
import { useHistory } from "react-router-dom";
import CustomButton from "../../../../components/CustomButton";
import { formatStreetLine } from "../../../../utils/propertyDisplay";
import { pushContactClick, pushScheduleStart } from "../../../../utils/dataLayer";

export type PropertyValues = {
  id: number;
  consultant_id: number;
  garbage_tax: string;
  rent_value: string;
  condominium_value: string;
  iptu_value: string;
  title: string;
  address: {
    address: string;
    number: number;
    neighborhood: string;
    city: string;
    uf: string;
  };
};

type Props = {
  propertyValues: PropertyValues;
  isScheduleDisabled?: boolean;
};

function calculateTotal(
  garbage_tax: string,
  rent_value: string,
  condominium_value: string,
  iptu_value: string
) {
  return (
    parseFloat(garbage_tax) +
    parseFloat(rent_value) +
    parseFloat(condominium_value) +
    parseFloat(iptu_value)
  );
}

function RentValuesCard({
  propertyValues,
  isScheduleDisabled = false,
}: Props) {
  const history = useHistory();

  function handleScheduleVisit() {
    if (isScheduleDisabled) {
      return;
    }

    pushScheduleStart({
      propertyId: propertyValues.id,
      title: propertyValues.title,
      rentValue: propertyValues.rent_value,
    });
    history.push(`/schedule/${propertyValues.id}`, {
      consultant_id: propertyValues.consultant_id,
      property_id: propertyValues.id,
      title: propertyValues.title,
      rent_value: propertyValues.rent_value,
      city: propertyValues.address.city,
      uf: propertyValues.address.uf,
      neighborhood: propertyValues.address.neighborhood,
    });
  }

  const whatsappPhoneNumber = "551140037654";
  const formattedRentValue = formatValue(parseFloat(propertyValues.rent_value));
  const fullAddress = [
    formatStreetLine(propertyValues.address, { includeStreetNumber: true }),
    propertyValues.address.neighborhood,
    `${propertyValues.address.city} - ${propertyValues.address.uf}`,
  ]
    .filter(Boolean)
    .join(", ");

  const whatsappMessage = encodeURIComponent(
    `Olá! Tenho interesse no ${propertyValues.title}, ${formattedRentValue}, ${fullAddress} que encontrei na 7Cantos: ${window.location.origin}/imovel/${propertyValues.id}. Código do anúncio na 7Cantos: ${propertyValues.id}`
  );

  return (
    <Wrapper>
      <Title>Informações do Aluguel</Title>
      <ValuesWrapper>
        <ValueWrapper>
          <FaPercentage />
          <TitlePriceWrapper>
            <CategoryTitle>Aluguel</CategoryTitle>
            <Price>{formatValue(parseFloat(propertyValues.rent_value))}</Price>
          </TitlePriceWrapper>
        </ValueWrapper>
        <ValueWrapper>
          <BiBuilding />
          <TitlePriceWrapper>
            <CategoryTitle>Condomínio</CategoryTitle>
            <Price>
              {formatValue(parseFloat(propertyValues.condominium_value))}
            </Price>
          </TitlePriceWrapper>
        </ValueWrapper>
        <ValueWrapper>
          <FaPercentage />
          <TitlePriceWrapper>
            <CategoryTitle>IPTU</CategoryTitle>
            <Price>{formatValue(parseFloat(propertyValues.iptu_value))}</Price>
          </TitlePriceWrapper>
        </ValueWrapper>
        <ValueWrapper>
          <FaPercentage />
          <TitlePriceWrapper>
            <CategoryTitle>TMSRU</CategoryTitle>
            <Price>{formatValue(parseFloat(propertyValues.garbage_tax))}</Price>
          </TitlePriceWrapper>
        </ValueWrapper>
      </ValuesWrapper>
      <Warnings>
        <MdInfoOutline /> Os valores do condomínio e do IPTU podem sofrer
        alterações.
      </Warnings>
      <TotalTitle>Total</TotalTitle>
      <TotalPrice>
        {formatValue(
          calculateTotal(
            propertyValues.garbage_tax,
            propertyValues.rent_value,
            propertyValues.condominium_value,
            propertyValues.iptu_value
          )
        )}
      </TotalPrice>
      <CustomButton
        type="button"
        size="giant"
        className="schedule-btn"
        disabled={isScheduleDisabled}
        aria-disabled={isScheduleDisabled}
        onClick={isScheduleDisabled ? undefined : handleScheduleVisit}
      >
        Agendar Visita
      </CustomButton>
      <ButtonsContainer>
        <a
          href="tel:+551140037654"
          onClick={() =>
            pushContactClick({ method: "phone", propertyId: propertyValues.id })
          }
        >
          <CustomButton variant="outline" size="giant" icon={<BiPhoneCall />}>
            Ligar
          </CustomButton>
        </a>
        <a
          href={`https://api.whatsapp.com/send?phone=${whatsappPhoneNumber}&text=${whatsappMessage}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            pushContactClick({ method: "whatsapp", propertyId: propertyValues.id })
          }
        >
          <CustomButton variant="outline" size="giant" icon={<FaWhatsapp />}>
            Whatsapp
          </CustomButton>
        </a>
      </ButtonsContainer>
    </Wrapper>
  );
}

export default RentValuesCard;